import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { FormErrorBanner } from '../shared/FormErrorBanner';
import { CountryMultiSelect } from '../shared/CountryMultiSelect';
import { CARRIER_STATUSES, type CarrierStatus } from '../../hooks/useCompaniesList';

export interface CompanyFormData {
  id?: number;
  name?: string;
  // Email is optional for Carrier Directory entries (#174) so admins
  // can save manually-created or imported carriers without forcing an
  // email on file. When present, the format must still be valid.
  email?: string;
  address?: string;
  phone_number?: string;
  country?: string;
  max_weight?: number;
  max_length?: number;
  max_width?: number;
  max_height?: number;
  delivery_countries?: string[];
  equipment_types?: string[];
  // Carrier Status (#173) replaces the legacy `active` boolean as the
  // source of truth on the form as well. `active` is intentionally not
  // part of the editable form payload; the backend derives it from
  // `carrier_status === "active"`.
  carrier_status?: CarrierStatus;
  // Carrier Directory profile fields (#174). All optional. The
  // `validated_by` user is captured server-side from the
  // authenticated admin, so it's not part of the form payload.
  postal_code?: string;
  city?: string;
  website?: string;
  direct_contact?: string;
  // The server serializes `validated_at` as ISO 8601 or null. The
  // form field edits the empty string when cleared, so `string |
  // null` mirrors the public surface exactly.
  validated_at?: string | null;
  notes?: string;
  other_equipment?: string;
}

export interface CompanyFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingCompany: { id: number; name: string } | null;
  formData: Partial<CompanyFormData>;
  onFormDataChange: (data: Partial<CompanyFormData>) => void;
  submitting: boolean;
  equipmentTypes: string[];
  onSubmit: () => void;
  onMultiSelectChange: (field: 'delivery_countries' | 'equipment_types', value: string) => void;
  fieldErrors: {
    name: string[];
    email: string[];
    country: string[];
    max_weight: string[];
    max_length: string[];
    max_width: string[];
    max_height: string[];
  };
  formErrors: string[];
}

export const CompanyFormModal: React.FC<CompanyFormModalProps> = ({
  isOpen,
  onOpenChange,
  editingCompany,
  formData,
  onFormDataChange,
  submitting,
  equipmentTypes,
  onSubmit,
  onMultiSelectChange,
  fieldErrors,
  formErrors,
}) => {
  const { t } = useTranslation();

  // The server serializes `validated_at` as an ISO 8601 UTC string
  // (e.g. "2026-05-01T12:00:00Z"). The `<input type="datetime-local">`
  // element has no timezone and expects a wall-clock string in the
  // admin's local zone ("YYYY-MM-DDTHH:MM"). Slicing the ISO string
  // would feed the UTC clock into a local-time input, so opening edit
  // and saving without touching the field would shift the stored
  // instant by the admin's timezone offset and churn `validated_by`.
  // Build the local wall-clock string via local Date getters so a
  // no-op save round-trips to the same UTC instant (#174 review
  // blocker).
  const validatedAtInputValue = (raw: string | null | undefined): string => {
    if (!raw) {
      return '';
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
      `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
    );
  };

  const renderFieldError = (errors: string[]) => {
    if (errors.length === 0) {
      return null;
    }

    return <p className="mt-2 text-sm text-destructive">{t(errors[0])}</p>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {editingCompany
                ? t('admin.companies.edit')
                : t('admin.companies.add')}
            </DialogTitle>
          </DialogHeader>

          <FormErrorBanner errors={formErrors} />

          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('admin.companies.name')} *</label>
                <input
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.name || ''}
                  onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
                  required
                />
                {renderFieldError(fieldErrors.name)}
              </div>
              <div className="space-y-2">
                {/* Email is optional for Carrier Directory entries (#174). */}
                <label className="text-sm font-medium">{t('admin.companies.email')}</label>
                <input
                  type="email"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.email || ''}
                  onChange={(e) => onFormDataChange({ ...formData, email: e.target.value })}
                />
                {renderFieldError(fieldErrors.email)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('admin.companies.country')} *</label>
                <input
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.country || ''}
                  onChange={(e) => onFormDataChange({ ...formData, country: e.target.value })}
                  placeholder={t('admin.companies.country_placeholder')}
                  required
                />
                {renderFieldError(fieldErrors.country)}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('admin.companies.phone')}</label>
                <input
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.phone_number || ''}
                  onChange={(e) => onFormDataChange({ ...formData, phone_number: e.target.value })}
                />
              </div>
            </div>

            {/* Carrier Directory profile fields (#174). Mirrors the
                spreadsheet source-of-truth so manually-created and
                imported carriers carry the same directory detail that
                operators maintain by hand. All fields are optional.
                Labels use `htmlFor`/`id` linkage so screen readers and
                `getByLabelText` can find the inputs reliably. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="company-postal-code" className="text-sm font-medium">{t('admin.companies.postalCode')}</label>
                <input
                  id="company-postal-code"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.postal_code || ''}
                  onChange={(e) => onFormDataChange({ ...formData, postal_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="company-city" className="text-sm font-medium">{t('admin.companies.city')}</label>
                <input
                  id="company-city"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.city || ''}
                  onChange={(e) => onFormDataChange({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="company-website" className="text-sm font-medium">{t('admin.companies.website')}</label>
                <input
                  id="company-website"
                  type="url"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.website || ''}
                  onChange={(e) => onFormDataChange({ ...formData, website: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="company-direct-contact" className="text-sm font-medium">{t('admin.companies.directContact')}</label>
                <input
                  id="company-direct-contact"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.direct_contact || ''}
                  onChange={(e) => onFormDataChange({ ...formData, direct_contact: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="company-validated-at" className="text-sm font-medium">{t('admin.companies.validatedAt')}</label>
              <input
                id="company-validated-at"
                type="datetime-local"
                className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={validatedAtInputValue(formData.validated_at)}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    // datetime-local has no timezone. Convert the
                    // admin-entered local moment to a proper UTC ISO
                    // string so the backend stores the same instant
                    // the admin selected; a naive `${value}:00Z`
                    // suffix would treat the local time as UTC and
                    // shift the rendered value by the admin's
                    // timezone offset (#174 review blocker).
                    validated_at: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : ''
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                {t('admin.companies.validatedAtHelp')}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('admin.companies.address')}</label>
              <textarea
                className="flex min-h-[60px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={formData.address || ''}
                onChange={(e) => onFormDataChange({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="company-notes" className="text-sm font-medium">{t('admin.companies.carrierNotes')}</label>
              <textarea
                id="company-notes"
                className="flex min-h-[80px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={formData.notes || ''}
                onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="company-other-equipment" className="text-sm font-medium">{t('admin.companies.otherEquipment')}</label>
              <textarea
                id="company-other-equipment"
                className="flex min-h-[60px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={formData.other_equipment || ''}
                onChange={(e) => onFormDataChange({ ...formData, other_equipment: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="carrier-status-select" className="text-sm font-medium">
                {t('admin.companies.carrierStatus')}
              </label>
              <select
                id="carrier-status-select"
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={formData.carrier_status ?? 'pending_validation'}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    carrier_status: e.target.value as CarrierStatus,
                  })
                }
              >
                {CARRIER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`admin.companies.carrierStatusLabels.${status}`, status)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {t('admin.companies.carrierStatusHelp')}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('admin.companies.equipmentTypes')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border p-3 rounded-md">
                {equipmentTypes.map(type => (
                  <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(formData.equipment_types || []).includes(type)}
                      onChange={() => onMultiSelectChange('equipment_types', type)}
                      className="rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    />
                    {t(`quote.methods.${type}`, type)}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-slate-500">{t('admin.companies.maxWeight')}</label>
                <input
                  type="number"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.max_weight ?? ''}
                  onChange={(e) => onFormDataChange({ ...formData, max_weight: e.target.value ? Number(e.target.value) : undefined })}
                />
                {renderFieldError(fieldErrors.max_weight)}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-slate-500">{t('admin.companies.maxLength')}</label>
                <input
                  type="number"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.max_length ?? ''}
                  onChange={(e) => onFormDataChange({ ...formData, max_length: e.target.value ? Number(e.target.value) : undefined })}
                />
                {renderFieldError(fieldErrors.max_length)}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-slate-500">{t('admin.companies.maxWidth')}</label>
                <input
                  type="number"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.max_width ?? ''}
                  onChange={(e) => onFormDataChange({ ...formData, max_width: e.target.value ? Number(e.target.value) : undefined })}
                />
                {renderFieldError(fieldErrors.max_width)}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-slate-500">{t('admin.companies.maxHeight')}</label>
                <input
                  type="number"
                  className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={formData.max_height ?? ''}
                  onChange={(e) => onFormDataChange({ ...formData, max_height: e.target.value ? Number(e.target.value) : undefined })}
                />
                {renderFieldError(fieldErrors.max_height)}
              </div>
            </div>

            {/* Delivery Countries (#302). Searchable worldwide-country
                multi-select backed by the same COUNTRIES list the homepage
                uses. Selections are stored as ISO alpha2 codes (the
                canonical shape quotes match against); chips render the
                readable country name. `htmlFor`/`id` linkage keeps the
                control discoverable by `getByLabelText` and screen
                readers. */}
            <div className="space-y-2">
              <label htmlFor="company-delivery-countries" className="text-sm font-medium">{t('admin.companies.deliveryCountries')}</label>
              <CountryMultiSelect
                id="company-delivery-countries"
                values={formData.delivery_countries || []}
                onChange={(values) => onFormDataChange({ ...formData, delivery_countries: values })}
                placeholder={t('admin.companies.delivery_countries_placeholder')}
                noResultsText={t('admin.companies.delivery_countries_no_results')}
                removeAriaLabel={(label) =>
                  t('admin.companies.delivery_countries_remove', { country: label })
                }
              />
              <p className="text-xs text-muted-foreground">{t('admin.companies.delivery_countries_help')}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.saving') : t('common.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
